import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
    Button,
    Card,
    Form,
    message,
    Modal,
    Select,
    Space,
    Spin,
    Table,
    Typography
} from "antd";
import {PlusOutlined, DeleteOutlined, FileTextOutlined} from "@ant-design/icons";
import {
    getGroupStudents,
    addStudentsToGroup,
    removeStudentFromGroup
} from "../../../../../../store/slices/MicroqualificationGroupsSlice";
import {format} from "date-fns";
import MicroqualificationGroupsService from "../../../../../../services/MicroqualificationGroupsService";
import UserService from "../../../../../../services/UserService";
import IntlMessage from "../../../../../../components/IntlMessage/IntlMessage";
import debounce from "lodash/debounce";

const {Text} = Typography;
const {confirm} = Modal;

const StudentsTab = ({groupId}) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [availableStudents, setAvailableStudents] = useState([]);
    const [loadingStudents, setLoadingStudents] = useState(false);

    const {students, studentsLoading} = useSelector(state => state.microqualificationGroups);

    useEffect(() => {
        if (groupId) {
            dispatch(getGroupStudents(groupId));
        }
    }, [groupId, dispatch]);

    const loadAvailableStudents = async (searchName = '') => {
        setLoadingStudents(true);
        try {
            let result;
            if (searchName) {
                result = await UserService.getStudentsSearchable(1, 100, searchName);
            } else {
                result = await UserService.getStudents(1, 100);
            }

            // Filter out students already in the group
            const studentIds = students.map(s => s.id);
            const filtered = result.data.filter(student => !studentIds.includes(student.id));

            setAvailableStudents(filtered.map(student => ({
                value: student.id,
                label: student.fullName || `${student.firstName} ${student.lastName}`
            })));
        } catch (error) {
            message.error('Failed to load students');
        } finally {
            setLoadingStudents(false);
        }
    };

    const fetchStudentSearch = debounce(async (searchText) => {
        await loadAvailableStudents(searchText);
    }, 500);

    const handleAddStudent = async () => {
        form.resetFields();
        setIsModalVisible(true);
        await loadAvailableStudents();
    };

    const handleDeleteStudent = (student) => {
        confirm({
            title: <IntlMessage id="mqGroups.removeStudent"/>,
            content: <IntlMessage id="mqGroups.confirmRemove"
                                  values={{name: `${student.firstName} ${student.lastName}`}}/>,
            okText: <IntlMessage id="delete"/>,
            okType: 'danger',
            cancelText: <IntlMessage id="cancel"/>,
            onOk: async () => {
                try {
                    await dispatch(removeStudentFromGroup({groupId, studentId: student.id})).unwrap();
                    message.success(<IntlMessage id="mqGroups.studentRemoved"/>);
                } catch (error) {
                    message.error(<IntlMessage id="mqGroups.failedToRemove"/>);
                }
            },
        });
    };

    const handleDownloadTranscript = async (student) => {
        try {
            const response = await MicroqualificationGroupsService.downloadTranscript(groupId, student.id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `transcript_${student.lastName}_${student.firstName}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success(<IntlMessage id="mqGroups.transcriptDownloaded"/>);
        } catch (error) {
            message.error(<IntlMessage id="mqGroups.failedToDownloadTranscript"/>);
        }
    };

    const onFinish = async (values) => {
        if (!values.students || values.students.length === 0) {
            message.warning('Please select at least one student');
            return;
        }

        setLoading(true);
        try {
            await dispatch(addStudentsToGroup({
                groupId,
                studentIds: values.students
            })).unwrap();

            message.success(<IntlMessage id="mqGroups.studentAdded"/>);
            setIsModalVisible(false);
            form.resetFields();

            // Refresh the students list
            dispatch(getGroupStudents(groupId));
        } catch (error) {
            if (error?.status === 409) {
                message.error(<IntlMessage id="mqGroups.studentAlreadyInGroup"/>);
            } else {
                message.error(error?.message || <IntlMessage id="mqGroups.failedToSave"/>);
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: '#',
            key: 'index',
            width: '5%',
            render: (_, __, index) => index + 1
        },
        {
            title: <IntlMessage id="mqGroups.fullName"/>,
            key: 'fullName',
            width: '25%',
            render: (_, record) => (
                <Text>
                    {record.fullNameEng || ''}
                </Text>
            )
        },
        {
            title: <IntlMessage id="iin"/>,
            dataIndex: 'iin',
            key: 'iin',
            width: '15%',
            render: (iin) => iin || '-'
        },
        {
            title: <IntlMessage id="email"/>,
            dataIndex: 'email',
            key: 'email',
            width: '20%'
        },
        {
            title: <IntlMessage id="mqGroups.phone"/>,
            dataIndex: 'telNumber',
            key: 'phone',
            width: '15%',
            render: (phone) => phone || '-'
        },
        {
            title: <IntlMessage id="mqGroups.birthDate"/>,
            dataIndex: 'birthDate',
            key: 'birthDate',
            width: '12%',
            render: (date) => date || '-'
        },
        {
            title: <IntlMessage id="actions"/>,
            key: 'actions',
            width: '8%',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<FileTextOutlined/>}
                        onClick={() => handleDownloadTranscript(record)}
                        title="Download Transcript"
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined/>}
                        onClick={() => handleDeleteStudent(record)}
                        title="Remove"
                    />
                </Space>
            )
        }
    ];

    return (
        <Card>
            <div style={{marginBottom: 16}}>
                <Button
                    type="primary"
                    icon={<PlusOutlined/>}
                    onClick={handleAddStudent}
                >
                    <IntlMessage id="mqGroups.addStudent"/>
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={students}
                loading={studentsLoading}
                rowKey="id"
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => <IntlMessage id="totalStudents" values={{count: total}}/>
                }}
            />

            <Modal
                title={<IntlMessage id="mqGroups.addStudent"/>}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="students"
                        label={<IntlMessage id="mqGroups.selectStudent"/>}
                        rules={[{required: true, message: <IntlMessage id="requiredField"/>}]}
                    >
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            filterOption={false}
                            onSearch={fetchStudentSearch}
                            loading={loadingStudents}
                            notFoundContent={loadingStudents ? <Spin size="small"/> : null}
                            placeholder={<IntlMessage id="mqGroups.selectStudent"/>}
                            options={availableStudents}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                <IntlMessage id="create"/>
                            </Button>
                            <Button onClick={() => {
                                setIsModalVisible(false);
                                form.resetFields();
                            }}>
                                <IntlMessage id="cancel"/>
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default StudentsTab;
